/**
 * ComplianceTimeline — unified chronological view of a website's
 * accessibility journey, merging score changes, statement versions,
 * and recurring monitoring scans into a single vertical timeline.
 */

import { useQuery } from "@tanstack/react-query";
import { apiGet, ApiCallError } from "../api/client.js";
import type { ScoreHistoryEntry } from "../api/types.js";
import type {
  StatementVersionSummary,
  RecurringRunSummary,
} from "../api/product-types.js";

/* ── Types ─────────────────────────────────────────────────────────────────── */

interface TimelineEvent {
  date: string;
  type: "score" | "statement" | "recurring";
  title: string;
  detail?: string;
  scoreValue?: number;
  scoreBand?: string;
}

/* ── Helpers ───────────────────────────────────────────────────────────────── */

const MAX_EVENTS = 20;

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("en-IE", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function bandLabel(band: string): string {
  switch (band) {
    case "strong":
      return "Strong";
    case "needs_improvement":
      return "Needs improvement";
    case "accessibility_risk":
      return "Accessibility risk";
    case "high_risk":
      return "High risk";
    default:
      return band;
  }
}

function iconForType(type: TimelineEvent["type"]): string {
  switch (type) {
    case "score":
      return "\u2B06"; // upwards arrow
    case "statement":
      return "\u2709"; // envelope (document)
    case "recurring":
      return "\u27F3"; // clockwise arrow (cycle)
  }
}

/* ── Event builders ────────────────────────────────────────────────────────── */

function buildScoreEvents(history: ScoreHistoryEntry[]): TimelineEvent[] {
  if (history.length === 0) return [];

  // History is expected oldest-first from the API; we walk forward to compute deltas.
  const sorted = [...history].sort(
    (a, b) =>
      new Date(a.completedAt).getTime() - new Date(b.completedAt).getTime(),
  );

  const events: TimelineEvent[] = [];

  for (let i = 0; i < sorted.length; i++) {
    const cur = sorted[i];
    const prev = i > 0 ? sorted[i - 1] : null;

    let title: string;
    let detail: string | undefined;

    if (prev) {
      const delta = cur.scoreValue - prev.scoreValue;
      const sign = delta >= 0 ? "+" : "";
      title = `Score changed from ${prev.scoreValue} to ${cur.scoreValue}`;
      detail = `${sign}${delta} points \u2014 ${bandLabel(cur.scoreBand)}`;
    } else {
      title = `Initial score: ${cur.scoreValue}`;
      detail = bandLabel(cur.scoreBand);
    }

    events.push({
      date: cur.completedAt,
      type: "score",
      title,
      detail,
      scoreValue: cur.scoreValue,
      scoreBand: cur.scoreBand,
    });
  }

  return events;
}

function buildStatementEvents(
  statements: StatementVersionSummary[],
): TimelineEvent[] {
  // Sort by generatedAt ascending, then assign version numbers
  const sorted = [...statements].sort(
    (a, b) =>
      new Date(a.generatedAt).getTime() - new Date(b.generatedAt).getTime(),
  );

  return sorted.map((s, idx) => ({
    date: s.generatedAt,
    type: "statement" as const,
    title: `Accessibility statement v${idx + 1} generated`,
    detail:
      s.scoreValue != null ? `Score at generation: ${s.scoreValue}` : undefined,
  }));
}

function buildRecurringEvents(runs: RecurringRunSummary[]): TimelineEvent[] {
  return runs
    .filter((r) => r.status === "success" || r.status === "partial")
    .map((r) => {
      const parts: string[] = [];
      if (r.issueGroupsNew > 0) parts.push(`${r.issueGroupsNew} new`);
      if (r.issueGroupsResolved > 0)
        parts.push(`${r.issueGroupsResolved} resolved`);
      if (parts.length === 0) parts.push("no changes");

      let detail: string | undefined;
      if (r.scoreDelta != null) {
        const sign = r.scoreDelta >= 0 ? "+" : "";
        detail = `Score: ${sign}${r.scoreDelta.toFixed(1)} points`;
      }

      return {
        date: r.completedAt ?? r.startedAt,
        type: "recurring" as const,
        title: `Monitoring scan: ${parts.join(", ")}`,
        detail,
      };
    });
}

/* ── Component ─────────────────────────────────────────────────────────────── */

export function ComplianceTimeline({ websiteId }: { websiteId: string }) {
  const {
    data: historyData,
    isLoading: historyLoading,
    isError: historyError,
  } = useQuery({
    queryKey: ["timeline", "scores", websiteId],
    queryFn: async () => {
      try {
        return await apiGet<{ history: ScoreHistoryEntry[] }>(
          `/websites/${websiteId}/scores/history`,
        );
      } catch (err) {
        if (err instanceof ApiCallError && err.status === 404)
          return { history: [] };
        throw err;
      }
    },
    enabled: !!websiteId,
    staleTime: 120_000,
  });

  const {
    data: statementsData,
    isLoading: statementsLoading,
    isError: statementsError,
  } = useQuery({
    queryKey: ["timeline", "statements", websiteId],
    queryFn: async () => {
      try {
        return await apiGet<StatementVersionSummary[]>(
          `/websites/${websiteId}/statements/list`,
        );
      } catch (err) {
        if (err instanceof ApiCallError && err.status === 404) return [];
        throw err;
      }
    },
    enabled: !!websiteId,
    staleTime: 120_000,
  });

  const {
    data: recurringData,
    isLoading: recurringLoading,
    isError: recurringError,
  } = useQuery({
    queryKey: ["timeline", "recurring", websiteId],
    queryFn: async () => {
      try {
        return await apiGet<RecurringRunSummary[]>(
          `/websites/${websiteId}/recurring/list`,
        );
      } catch (err) {
        if (err instanceof ApiCallError && err.status === 404) return [];
        throw err;
      }
    },
    enabled: !!websiteId,
    staleTime: 120_000,
  });

  const isLoading = historyLoading || statementsLoading || recurringLoading;

  if (isLoading) {
    return (
      <p className="timeline-empty" role="status">
        Loading timeline...
      </p>
    );
  }

  // Build events from whichever sources succeeded
  const events: TimelineEvent[] = [];

  if (!historyError && historyData?.history) {
    events.push(...buildScoreEvents(historyData.history));
  }
  if (!statementsError && statementsData) {
    events.push(...buildStatementEvents(statementsData));
  }
  if (!recurringError && recurringData) {
    events.push(...buildRecurringEvents(recurringData));
  }

  if (events.length === 0) {
    return (
      <p className="timeline-empty">
        No compliance events yet. Run a scan to start tracking your
        accessibility journey.
      </p>
    );
  }

  // Sort newest-first and cap at MAX_EVENTS
  events.sort(
    (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
  );
  const visible = events.slice(0, MAX_EVENTS);

  return (
    <div
      className="compliance-timeline"
      role="list"
      aria-label="Compliance timeline"
    >
      {visible.map((ev, i) => (
        <div
          className="timeline-event"
          role="listitem"
          key={`${ev.type}-${ev.date}-${i}`}
        >
          <span
            className={`timeline-event__dot timeline-event__dot--${ev.type}`}
            aria-hidden="true"
          >
            {iconForType(ev.type)}
          </span>
          <time className="timeline-event__date" dateTime={ev.date}>
            {formatDate(ev.date)}
          </time>
          <p className="timeline-event__title">{ev.title}</p>
          {ev.detail && <p className="timeline-event__detail">{ev.detail}</p>}
        </div>
      ))}
      {events.length > MAX_EVENTS && (
        <p className="timeline-event__detail">
          Showing {MAX_EVENTS} of {events.length} events.
        </p>
      )}
    </div>
  );
}
